import { Button, Divider, Input, InputNumber, Modal, Space, Switch, Tabs, Tooltip } from 'antd';
import { setTrajectoryStore } from '../store/entities/trajectory';
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCog } from '@fortawesome/free-solid-svg-icons/faUserCog';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { Milliseconds } from '../util/units';
import { QuestionCircleOutlined } from '@ant-design/icons';

const { TabPane } = Tabs;

const useDarkMode = (localStorage.getItem('darkMode') === 'true');
const MIN_SLIDESHOW_SPEED_WITH_SCREENSHOTS: Milliseconds = 500 as Milliseconds;

export const SettingsModal = () => {
  const dispatch = useAppDispatch();
  // const showOnlyPersonalRoutes = useAppSelector(state => state.entities.trajectory.showOnlyPersonalRoutes);
  const isLiveModeEnabled = useAppSelector(state => state.entities.trajectory.isLiveModeEnabled);
  const showSplitLines = useAppSelector(state => state.entities.trajectory.showSplitLines);
  const slideshowSpeed = useAppSelector(state => state.entities.trajectory.slideshowSpeed);
  const slideshowSaveScreenshots = useAppSelector(state => state.entities.trajectory.slideshowSaveScreenshots);
  const slideshowScreenshotFileName = useAppSelector(state => state.entities.trajectory.slideshowScreenshotFileName);
  const maxDistanceFromEnds = useAppSelector(state => state.entities.trajectory.maxDistanceFromEnds);
  const organizationName = useAppSelector(state => state.entities.trajectory.organizationName);
  const role = useAppSelector(state => state.entities.trajectory.role);

  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    localStorage.setItem('showSplitLines', showSplitLines ? 'true' : 'false');
    localStorage.setItem('slideshowSpeed', slideshowSpeed + '');
    localStorage.setItem('slideshowSaveScreenshots', slideshowSaveScreenshots ? 'true' : 'false');
    localStorage.setItem('isLiveModeEnabled', isLiveModeEnabled ? 'true' : 'false');
    localStorage.setItem('slideshowScreenshotFileName', slideshowScreenshotFileName);
    localStorage.setItem('maxDistanceFromEnds', `${maxDistanceFromEnds}`);
  }, [showSplitLines, slideshowSpeed, slideshowSaveScreenshots, slideshowScreenshotFileName,
    isLiveModeEnabled, maxDistanceFromEnds]);

  // const onShowOnlyPersonalRouteChange = (checked: boolean) => dispatch(setTrajectoryStore({ showOnlyPersonalRoutes: checked }));

  const onShowSplitLinesChanged = (checked: boolean) => dispatch(setTrajectoryStore({ showSplitLines: checked }));

  const onIsLiveModeEnabledChanged = (checked: boolean) => dispatch(setTrajectoryStore({ isLiveModeEnabled: checked }));

  const onDarkModeChanged = (checked: boolean) => {
    localStorage.setItem('darkMode', checked ? 'true' : 'false');
    window.location.reload();
  };

  const onSlideshowSaveScreenshotsChange = (checked: boolean) => {
    dispatch(setTrajectoryStore({
      slideshowSaveScreenshots: checked,
      ...(slideshowSpeed < MIN_SLIDESHOW_SPEED_WITH_SCREENSHOTS && { slideshowSpeed: MIN_SLIDESHOW_SPEED_WITH_SCREENSHOTS }),
    }));
  };

  const onSlideshowSpeedChange = (value: number) => dispatch(setTrajectoryStore({ slideshowSpeed: value * 1000 }));

  const onResetMaxDistanceFromEnds = () => dispatch(setTrajectoryStore({ maxDistanceFromEnds: 0.25 }));

  const onResetAllSettings = () => {
    dispatch(setTrajectoryStore({
      maxDistanceFromEnds: 0.25,
      slideshowScreenshotFileName: 'slideshow_screenshot',
      slideshowSpeed: 1000,
      slideshowSaveScreenshots: false,
      showSplitLines: true,
      showOnlyMyRoutes: false,
    }));
  };

  return (
    <div>
      <Button icon={<FontAwesomeIcon icon={faUserCog}/>} onClick={() => setModalVisible(true)}/>
      <Modal
        title="Settings (auto-saved)"
        visible={modalVisible}
        footer={null}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Space direction='vertical' style={{ width: '100%' }}>
          <Tabs defaultActiveKey={'general'} size={'small'} style={{ marginTop: 0 }}>
            <TabPane tab={'General'} key={'general'}>
              <Space direction={'vertical'}>
                <Tooltip title="Shows vertical split lines on main plot">
                  <Space>
                    <Switch
                      onChange={onShowSplitLinesChanged}
                      defaultChecked={showSplitLines}
                      style={{ justifyContent: 'flex-end' }}
                    />
                    <div>Show split lines</div>
                  </Space>
                </Tooltip>
                <Space>
                  <Switch
                    defaultChecked={useDarkMode}
                    onChange={onDarkModeChanged}
                  />
                  <div>Dark mode (Reloads page)</div>
                </Space>
              </Space>
            </TabPane>
            <TabPane tab={'Slideshow'} key={'slideshow'}>
              <Space direction={'vertical'}>

                <Tooltip title="Screenshots can be up to ~2 MB in size!">
                  <Space>
                    <Switch
                      defaultChecked={slideshowSaveScreenshots}
                      onChange={onSlideshowSaveScreenshotsChange}/>
                    Save screenshots
                  </Space>
                </Tooltip>
                <Space direction='horizontal'>
                  <div className="input-label">Slideshow speed</div>
                  <InputNumber
                    min={(slideshowSaveScreenshots) ? (MIN_SLIDESHOW_SPEED_WITH_SCREENSHOTS / 1000) : 0.1}
                    step={0.1}
                    value={slideshowSpeed / 1000}
                    onChange={onSlideshowSpeedChange}
                  />
                  <div>s</div>
                </Space>
                <Space>
                  <div className="input-label">Screenshot file name</div>
                  <Input
                    value={slideshowScreenshotFileName}
                    suffix='_{Route}_{Start}_{End}_{ID}.png'
                    onChange={e => {
                      dispatch(setTrajectoryStore({ slideshowScreenshotFileName: e.target.value }));
                    }}/>
                </Space>
              </Space>
            </TabPane>
            <TabPane tab={'Advanced'} key={'advanced'}>
              <Space direction={'vertical'}>
                <Space>
                  <div className={'input-label'}>Travel Time Threshold</div>
                  <InputNumber
                    min={0.001}
                    step={0.01}
                    value={maxDistanceFromEnds}
                    onChange={value => dispatch(setTrajectoryStore({ maxDistanceFromEnds: value }))}
                  />
                  <div>miles</div>
                  <Tooltip title={'Truck data always uses 1.5 miles.'}>
                    <QuestionCircleOutlined style={{ marginTop: '5px' }}/>
                  </Tooltip>
                  <Divider type={'vertical'}/>
                  <Button
                    type={'link'}
                    danger={true}
                    style={{ paddingLeft: 0, paddingRight: 0 }}
                    onClick={onResetMaxDistanceFromEnds}
                  >Reset</Button>
                </Space>
                <Space>
                  <Switch
                    defaultChecked={isLiveModeEnabled}
                    disabled={(organizationName !== 'JTRP'
                      || (role ? !['administrator', 'owner'].includes(role) : false)) && !isLiveModeEnabled}
                    onChange={onIsLiveModeEnabledChanged}
                  />
                  Live mode
                </Space>
                <Button
                  style={{ marginTop: '6px' }}
                  danger={true}
                  onClick={onResetAllSettings}
                >Reset all settings</Button>
              </Space>
            </TabPane>
          </Tabs>
        </Space>
      </Modal>
    </div>
  );
};
